import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { Sparkles, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import * as THREE from 'three';

function FloatingParticles() {
  const meshRef = useRef();

  useEffect(() => {
    if (!meshRef.current) return;
    
    const positions = meshRef.current.geometry.attributes.position.array;
    const count = positions.length / 3;
    
    for (let i = 0; i < count; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 10;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    
    meshRef.current.geometry.attributes.position.needsUpdate = true;
  }, []);

  useEffect(() => {
    const animate = () => {
      if (meshRef.current) {
        meshRef.current.rotation.y += 0.001;
        meshRef.current.rotation.x += 0.0005;
      }
      requestAnimationFrame(animate);
    };
    animate();
  }, []);

  return (
    <points ref={meshRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={500}
          array={new Float32Array(500 * 3)}
          itemSize={3}
        />
      </bufferGeometry>
      <pointsMaterial
        size={0.05}
        color="#000"
        transparent
        opacity={0.6}
        sizeAttenuation
      />
    </points>
  );
}

export const Hero = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: false, amount: 0.3 });
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: '#f7f5f2' }}
    >
      {/* 3D Background */}
      <div className="absolute inset-0 opacity-40">
        <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} />
          <FloatingParticles />
        </Canvas>
      </div>

      {/* Content */}
      <motion.div
        style={{ y, opacity }}
        className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.8, ease: [0.4, 0, 0.3, 1] }}
          className="mb-6 inline-flex items-center space-x-2 px-4 py-2 rounded-full"
          style={{ backgroundColor: 'rgba(97, 82, 90, 0.1)' }}
        >
          <Sparkles size={20} style={{ color: '#000' }} />
          <span style={{ color: '#000' }} className="font-medium">
            The Future of Event Management
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.2, ease: [0.4, 0, 0.3, 1] }}
          className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
          style={{ color: '#283750' }}
        >
          Manage Events
          <br />
          <span style={{ color: '#000' }}>Like Never Before</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.4, 0, 0.3, 1] }}
          className="text-xl md:text-2xl mb-10 max-w-3xl mx-auto"
          style={{ color: '#736c64' }}
        >
          Create stunning events, manage ticketing, check-in attendees, and analyze
          performance—all from one powerful platform.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.6, ease: [0.4, 0, 0.3, 1] }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4"
        >
          <Button
            size="lg"
            className="text-lg px-8 py-6 font-medium transition-all duration-300 hover:scale-105 group"
            style={{ backgroundColor: '#000', color: 'white' }}
          >
            Start Free Trial
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="text-lg px-8 py-6 font-medium transition-all duration-300 hover:scale-105"
            style={{ borderColor: '#000', color: '#000' }}
          >
            Watch Demo
          </Button>
        </motion.div>

        {/* Floating Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isInView ? 1 : 0, y: isInView ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.4, 0, 0.3, 1] }}
          className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto"
        >
          {[
            { value: '50K+', label: 'Events' },
            { value: '2M+', label: 'Attendees' },
            { value: '98%', label: 'Satisfaction' },
            { value: '24/7', label: 'Support' },
          ].map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: isInView ? 1 : 0,
                scale: isInView ? 1 : 0.8,
              }}
              transition={{ duration: 0.5, delay: 0.9 + index * 0.1 }}
              className="text-center"
            >
              <div
                className="text-3xl md:text-4xl font-bold mb-2"
                style={{ color: '#000' }}
              >
                {stat.value}
              </div>
              <div className="text-sm" style={{ color: '#736c64' }}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-6 h-10 border-2 rounded-full flex items-start justify-center p-2"
          style={{ borderColor: '#000' }}
        >
          <motion.div
            animate={{ height: ['0%', '40%', '0%'] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1 rounded-full"
            style={{ backgroundColor: '#000' }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
